// libs
import { useCallback, useEffect, useMemo, useState } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { AllUpiIntentAvailableMethodsResponse } from "@swiggy-private/payment-types";
import { SESSION_STORAGE_KEYS, Storage, captureError } from "@swiggy-private/core-ui";

// helpers
import { shouldShowSyntheticPPM, transformResponseForNuPPm } from "@payment-client/helpers/SyntheticPaymentGroupHelper";
import {
    checkAndRecordAmountMismatch,
    transformPaymentResponseForTurboPay,
} from "@payment-client/helpers/TurboPayment";
import {
    getUpiIntentMethods,
    allUpiIntentAvailableMethods,
    shouldShowThirdPartyUpiIntentApps,
    processAndGetFinalUpiIntentData,
} from "@payment-client/helpers/UpiHelper";
import { upiIntentAvailableMethods } from "@payment-client/helpers/PaymentHelper";

// hooks
import { ShowPaymentState } from "@payment-client/interfaces/ShowPayments";

// types
import { ShowPaymentContextValue } from "@payment-client/contexts/ShowPaymentContext";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

export function useTransformPaymentResponse({
    showPaymentState,
    isTurboPaymentMethod,
    reloadPayments,
    refetchPayments,
    isPaymentMethodSelectedForOnce,
    setIsPaymentMethodSelectedForOnce,
}: {
    showPaymentState: ShowPaymentState;
    isTurboPaymentMethod: boolean;
    reloadPayments: () => void;
    refetchPayments: () => void;
    isPaymentMethodSelectedForOnce: boolean;
    setIsPaymentMethodSelectedForOnce: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const originalPaymentResponse = useMemo(
        () => showPaymentState?.data?.map(res => res.paymentResponse).unwrapOr({} as GetPaymentMethodsResponse),
        [showPaymentState?.data],
    );

    const shouldAddSyntheticPPM = shouldShowSyntheticPPM(originalPaymentResponse);

    const upiIntentMethodsList = useMemo(
        () => (!isEmpty(originalPaymentResponse) ? getUpiIntentMethods(originalPaymentResponse) : []),
        [originalPaymentResponse],
    );

    const [availableUpiIntentApps, setAvailableUpiIntentApps] = useState<string[]>([]);

    const fetchAvailableMethods = useCallback(async () => {
        /**
         * This is actually an extra call to the native bridge, its required in order not to alter the existing
         * upiIntentValue flow. Altering it may have side effects, which we cannot predict or trace back to the
         * original changes.
         */
        if (shouldShowThirdPartyUpiIntentApps(originalPaymentResponse)) {
            allUpiIntentAvailableMethods(upiIntentMethodsList)
                .then(res => {
                    const upiIntentData = processAndGetFinalUpiIntentData(
                        originalPaymentResponse as GetPaymentMethodsResponse,
                        res as AllUpiIntentAvailableMethodsResponse,
                    );

                    Storage.setItem(
                        SESSION_STORAGE_KEYS.THIRD_PARTY_UPI_INTENT_APPS_DETAILS,
                        upiIntentData.thirdPartyUpiIntentAppsDetails,
                    );

                    setAvailableUpiIntentApps(upiIntentData.sortedUpiApps);
                })
                .catch(err => {
                    captureError(err);
                });
        } else {
            upiIntentAvailableMethods(upiIntentMethodsList)
                .then(res => {
                    setAvailableUpiIntentApps(res);
                })
                .catch(err => {
                    captureError(err);
                });
        }
    }, [upiIntentMethodsList, originalPaymentResponse]);

    useEffect(() => {
        if (!isEmpty(upiIntentMethodsList) && shouldAddSyntheticPPM) {
            fetchAvailableMethods();
        }
    }, [fetchAvailableMethods, shouldAddSyntheticPPM, upiIntentMethodsList]);

    const paymentResponse = useMemo<ShowPaymentContextValue>(() => {
        let paymentsError: ShowPaymentContextValue["paymentsError"];

        if (showPaymentState.data?.isErr()) {
            paymentsError = showPaymentState.data?.err().unwrapOr(new Error());
        }

        const { paymentResponse = null, checkoutMetaInfo = null, _rawPaymentResponse = null } =
            showPaymentState.data
                ?.map<Pick<ShowPaymentContextValue, "paymentResponse" | "checkoutMetaInfo" | "_rawPaymentResponse">>(
                    res => {
                        const responseWithSyntheticPPM = shouldAddSyntheticPPM
                            ? transformResponseForNuPPm(res.paymentResponse, availableUpiIntentApps)
                            : res.paymentResponse;

                        const transformedResponseWithTurboPay = transformPaymentResponseForTurboPay(
                            responseWithSyntheticPPM,
                            isTurboPaymentMethod,
                        );

                        checkAndRecordAmountMismatch(transformedResponseWithTurboPay, isTurboPaymentMethod);

                        return {
                            paymentResponse: transformedResponseWithTurboPay,
                            checkoutMetaInfo: res.checkoutMetaInfo.isSome() ? res.checkoutMetaInfo.unwrap() : null,
                            // _rawPaymentResponse is used in TurboPlaceOrder component for placing order using turbo flows in SDK
                            _rawPaymentResponse: res.paymentResponse,
                        };
                    },
                )
                .unwrapOr({}) ?? {};
        return {
            paymentResponse,
            checkoutMetaInfo,
            reloadPayments,
            refetchPayments,
            isPaymentMethodSelectedForOnce,
            setIsPaymentMethodSelectedForOnce,
            loadingPayments: showPaymentState.loading,
            _rawPaymentResponse,
            paymentsError,
        };
    }, [
        showPaymentState.data,
        showPaymentState.loading,
        reloadPayments,
        refetchPayments,
        isPaymentMethodSelectedForOnce,
        setIsPaymentMethodSelectedForOnce,
        shouldAddSyntheticPPM,
        availableUpiIntentApps,
        isTurboPaymentMethod,
    ]);

    return paymentResponse;
}
